/**
 * المرحلة 1.3 (P10, M02) — the doctor's page, as a patient reads it.
 *
 * GET /api/doctors/:id/public: the name, the title and picture, the
 * specialty, the bio in the reader's language (the other one when it is all
 * there is), the languages, the qualifications, the years of experience, the
 * channels the doctor may be booked for, each clinic with its price, the
 * soonest free slot, the rating and the published reviews — each as the
 * doctor's institution's policies show them (a rating below the minimum
 * count is not shown; a hidden price stays on the booking page).
 *
 * A doctor the patients' list does not show (a hidden specialty, a profile
 * still waiting for its review) is 404 here too. The doctor previews their
 * own page from «ملفي المهني» (`previewId`): the server shows it to them
 * whatever the policy, with what is still missing.
 */

import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Avatar, Badge, EmptyState, ErrorState, Icon, PageHeader, Panel, Spinner } from '../../components/common';
import { doctorApi } from '../../api/endpoints';
import { formatCurrency } from '../../domain/pricing';
import { formatDate } from '../../domain/locale';
import { categoryName } from '../../domain/catalog';
import { useData } from '../../context/DataContext';
import { useI18n } from '../../i18n/I18nProvider';
import { ModeBadge, RatingLine, SoonestLine, Stars } from '../discovery/parts';

export function DoctorProfilePage({ previewId = null }) {
  const params = useParams();
  const doctorId = previewId || params.doctorId;
  const preview = Boolean(previewId);
  const { t, lang } = useI18n();
  const { categories } = useData();
  const [state, setState] = useState({ loading: true, error: null, doctor: null });

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    doctorApi
      .publicProfile(doctorId)
      .then((doctor) => setState({ loading: false, error: null, doctor }))
      .catch((error) => setState({ loading: false, error, doctor: null }));
  };
  useEffect(load, [doctorId]);

  if (state.loading) return <Spinner label={t('doctorPage.loading')} />;
  if (state.error && state.error.status === 404) {
    return (
      <Panel>
        <EmptyState
          icon="user-x"
          title={t('doctorPage.notFound')}
          message={t('doctorPage.notFoundMessage')}
          action={preview ? null : <Link to="/patient/doctors" className="btn-primary">{t('doctorPage.backToList')}</Link>}
        />
      </Panel>
    );
  }
  if (state.error) return <ErrorState error={state.error.message} onRetry={load} />;

  const d = state.doctor;
  const bio = (lang === 'en' ? d.bioEn || d.bioAr : d.bioAr || d.bioEn) || null;
  const specialty = (lang === 'en' && d.categoryNameEn) || d.categoryName || categoryName(d.categoryId, categories, lang);
  const prices = d.clinics.filter((c) => c.examPrice !== null);

  return (
    <div className="space-y-6" data-testid="doctor-page">
      <PageHeader title={d.name} subtitle={d.title || specialty} icon="user-round" />

      {preview && (
        <p className="info-note">
          <Icon name="eye" size={14} className="mt-0.5 shrink-0" />
          <span>
            {t('doctorPage.previewNote')}
            {Array.isArray(d.missing) && d.missing.length > 0 && ` ${t('proProfile.missing', { fields: d.missing.map((f) => t(`proProfile.field.${f}`)).join(t('proProfile.joiner')) })}`}
          </span>
        </p>
      )}

      <Panel>
        <div className="dp-hero">
          <Avatar src={d.image} name={d.name} size={88} />
          <div className="min-w-0 space-y-2">
            <div className="flex flex-wrap items-center gap-2">
              <Badge className="border-cyan-500/30 bg-cyan-500/10 text-cyan-300">{specialty}</Badge>
              {d.modes.map((m) => (
                <ModeBadge key={m} mode={m} />
              ))}
            </div>
            <RatingLine avg={d.ratingAvg} count={d.ratingCount} />
            <div>
              <SoonestLine soonest={d.soonest} />
            </div>
          </div>
        </div>

        <div className="dp-facts mt-5">
          <Fact icon="briefcase-medical" label={t('doctorPage.experience')} value={d.yearsOfExperience !== null ? t('discovery.card.experience', { n: d.yearsOfExperience }) : '—'} />
          <Fact
            icon="languages"
            label={t('doctorPage.languages')}
            value={d.languages.length ? d.languages.map((code) => t(`doctorPage.lang.${code}`)).join(lang === 'en' ? ', ' : '، ') : '—'}
          />
          <Fact icon="building-2" label={t('doctorPage.clinics')} value={String(d.clinics.length)} />
        </div>

        <div className={`mt-5 flex flex-wrap justify-end gap-2 ${preview ? 'hidden' : ''}`}>
          <Link to="/patient/doctors" className="btn-ghost">
            {t('doctorPage.backToList')}
          </Link>
          <Link to={`/patient/book/${d.id}`} className={d.clinics.length ? 'btn-primary' : 'btn-ghost pointer-events-none opacity-50'} data-testid="doctor-page-book">
            <Icon name="calendar-plus" size={16} />
            {t('discovery.card.book')}
          </Link>
        </div>
      </Panel>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        <Panel>
          <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-white">
            <Icon name="user-round" size={18} className="text-cyan-400" />
            {t('doctorPage.about')}
          </h3>
          {bio ? <p className="dp-bio" dir="auto">{bio}</p> : <p className="text-sm text-slate-400">{t('doctorPage.noBio')}</p>}

          {d.qualifications.length > 0 && (
            <>
              <h4 className="mb-2 mt-5 flex items-center gap-2 text-sm font-bold text-white">
                <Icon name="graduation-cap" size={16} className="text-purple-400" />
                {t('doctorPage.qualifications')}
              </h4>
              <ul className="dp-quals">
                {d.qualifications.map((q, index) => (
                  <li key={`${q.title}-${index}`}>
                    <Icon name="badge-check" size={15} className="mt-0.5 shrink-0 text-emerald-400" />
                    <span dir="auto">
                      <strong className="text-white">{q.title}</strong>
                      {q.place ? ` — ${q.place}` : ''}
                      {q.year ? ` (${q.year})` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            </>
          )}
        </Panel>

        <Panel>
          <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-white">
            <Icon name="map-pin" size={18} className="text-emerald-400" />
            {t('doctorPage.clinicsAndPrices')}
          </h3>
          <div className="space-y-2">
            {d.clinics.map((c) => (
              <div key={c.id} className="rounded-xl bg-white/5 p-3">
                <p className="text-sm font-bold text-white" dir="auto">{c.name}</p>
                {c.address && <p className="text-xs text-slate-400" dir="auto">{c.address}</p>}
                {c.examPrice !== null && (
                  <p className="mt-1 text-xs text-slate-300">
                    {t('discovery.card.exam')}: <strong className="text-white">{formatCurrency(c.examPrice)}</strong>
                    {' · '}
                    {t('discovery.card.followup')}: <strong className="text-white">{formatCurrency(c.followupPrice)}</strong>
                  </p>
                )}
              </div>
            ))}
            {prices.length === 0 && d.clinics.length > 0 && <p className="text-xs text-slate-400">{t('discovery.card.priceAtBooking')}</p>}
            {d.modes.some((m) => m !== 'in_person') && (
              <p className="info-note">
                <Icon name="info" size={14} className="mt-0.5 shrink-0" />
                {t('doctorPage.remoteNote', {
                  video: d.modePercent.video,
                  voice: d.modePercent.voice,
                })}
              </p>
            )}
          </div>
        </Panel>
      </div>

      <Panel>
        <h3 className="mb-3 flex items-center gap-2 text-base font-bold text-white">
          <Icon name="message-square-text" size={18} className="text-amber-400" />
          {t('doctorPage.reviews')}
        </h3>
        {d.reviews.length === 0 ? (
          <p className="text-sm text-slate-400">{d.ratingAvg === null ? t('doctorPage.reviewsHidden') : t('doctorPage.noReviews')}</p>
        ) : (
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {d.reviews.map((r) => (
              <div key={r.id} className="dp-review">
                <div className="flex items-center justify-between gap-2">
                  <Stars value={r.rating} size={14} />
                  <span className="text-[11px] text-slate-500">{formatDate(r.at)}</span>
                </div>
                {r.comment && <p className="mt-2 text-sm leading-relaxed text-slate-300" dir="auto">{r.comment}</p>}
                <p className="mt-1 text-[11px] text-slate-500">{r.by || t('doctorPage.aPatient')}</p>
              </div>
            ))}
          </div>
        )}
      </Panel>
    </div>
  );
}

function Fact({ icon, label, value }) {
  return (
    <div className="dp-fact">
      <span className="dp-fact__label">
        <Icon name={icon} size={13} />
        {label}
      </span>
      <p className="dp-fact__value">{value}</p>
    </div>
  );
}

export default DoctorProfilePage;
